import React, { useCallback } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
} from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import useSortTable from "../services/useSortTable";

interface UnmatchTableProps {
  tableData: PhaseTableRow[];
  selectedRows: string[];
  allColumns: { header: string; accessor: string }[];
  loading: boolean;
  error: any;
  unmatchLoading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  handleSelectRow: (fxUniqueId: string) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUnmatch: () => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface PhaseTableRow {
  FX_UNIQUE_ID?: string;
  [key: string]: unknown;
}

const UnmatchTable: React.FC<UnmatchTableProps> = ({
  tableData,
  selectedRows,
  allColumns,
  loading,
  error,
  unmatchLoading,
  totalCount,
  page,
  rowsPerPage,
  handleSelectRow,
  handleSelectAll,
  handleUnmatch,
  handleChangePage,
  handleChangeRowsPerPage,
}) => {
  const setPage = useCallback(
    (newPage: number) => {
      handleChangePage(null, newPage);
    },
    [handleChangePage]
  );

  const { sortedRows, sortField, sortDirection, handleSort } = useSortTable(tableData, setPage);

  return (
    <MDBox>
      {loading && <MDTypography>Loading...</MDTypography>}
      {error && <MDTypography color="error">Error: {error.message}</MDTypography>}

      {!loading && !error && sortedRows.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 800 }}>
            <Table sx={{ minWidth: 650 }} aria-label="unmatch table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {sortedRows.length > 0 && (
                      <Checkbox
                        checked={selectedRows.length === sortedRows.length && sortedRows.length > 0}
                        onChange={handleSelectAll}
                        sx={{
                          color: colors.primary.main,
                          "&.Mui-checked": {
                            color: colors.secondary.main,
                            border: "none",
                          },
                          "&:hover": {
                            color: colors.primary.dark,
                          },
                          border: `1px solid ${colors.primary.main}`,
                          borderRadius: "4px",
                          padding: "0",
                          marginRight: "0.2rem",
                        }}
                      />
                    )}
                  </TableCell>
                  {allColumns
                    ?.slice(1)
                    ?.filter((column) => column.accessor !== "RN")
                    ?.map((column) => (
                      <TableCell
                        key={column.accessor}
                        sx={{
                          fontWeight: "bold",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: colors.grey[400],
                          },
                        }}
                        onClick={() => handleSort(column.accessor)}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {column.header}
                          {sortField === column.accessor && (
                            <span style={{ marginLeft: 4 }}>
                              {sortDirection === "ASC" ? (
                                <ArrowUpwardIcon fontSize="small" />
                              ) : (
                                <ArrowDownwardIcon fontSize="small" />
                              )}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows?.map((row) => {
                  const rowId = row.FX_UNIQUE_ID ? String(row.FX_UNIQUE_ID) : "";
                  return (
                    <TableRow key={rowId}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(rowId)}
                          onChange={() => handleSelectRow(rowId)}
                          sx={{
                            color: colors.primary.main,
                            "&.Mui-checked": {
                              color: colors.secondary.main,
                              border: "none",
                            },
                            "&:hover": {
                              color: colors.primary.dark,
                            },
                            border: `1px solid ${colors.primary.main}`,
                            borderRadius: "4px",
                            padding: "0",
                            marginRight: "0.2rem",
                          }}
                        />
                      </TableCell>
                      {allColumns
                        ?.slice(1)
                        ?.filter((column) => column.accessor !== "RN")
                        ?.map((column) => (
                          <TableCell key={`${rowId}-${column.accessor}`}>
                            {String(row[column.accessor] ?? "-")}
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>

          <MDBox mt={2} display="flex" gap={2}>
            <MDButton
              variant="gradient"
              color="error"
              disabled={selectedRows.length === 0 || unmatchLoading}
              onClick={handleUnmatch}
            >
              {unmatchLoading ? "Unmatching..." : `Unmatch Selected (${selectedRows.length})`}
            </MDButton>
          </MDBox>
        </>
      ) : (
        !loading &&
        !error && (
          <MDBox display="flex" justifyContent="center" alignItems="center" marginTop={20}>
            <MDTypography variant="h3" color="text">
              No data to unmatch
            </MDTypography>
          </MDBox>
        )
      )}
    </MDBox>
  );
};

export default UnmatchTable;
